import { useMemo } from 'react';
import { ExternalLink } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { formatBytes, formatTime, extractDomain } from '@har-insight/parser';

interface DomainStats {
  domain: string;
  isFirstParty: boolean;
  count: number;
  totalSize: number;
  totalTime: number;
}

export function ThirdPartyBreakdown() {
  const { filteredEntries, har } = useHARStore();

  const { firstParty, thirdParty } = useMemo(() => {
    if (filteredEntries.length === 0) {
      return { firstParty: [], thirdParty: [], primaryDomain: '' };
    }

    // Determine primary domain from pages or first request
    const primaryDomain = har?.log.pages?.[0]?.title
      ? extractDomain(har.log.pages[0].title)
      : extractDomain(filteredEntries[0].request.url);

    // Get root domain (e.g., example.com from www.example.com)
    const getRootDomain = (domain: string) => {
      const parts = domain.split('.');
      if (parts.length > 2) {
        return parts.slice(-2).join('.');
      }
      return domain;
    };

    const primaryRoot = getRootDomain(primaryDomain);

    // Group by domain
    const domainMap = new Map<string, DomainStats>();

    filteredEntries.forEach(entry => {
      const domain = extractDomain(entry.request.url);
      const domainRoot = getRootDomain(domain);
      const isFirstParty = domainRoot === primaryRoot;

      const existing = domainMap.get(domain);
      if (existing) {
        existing.count++;
        existing.totalSize += entry.response.content.size || 0;
        existing.totalTime += entry.time || 0;
      } else {
        domainMap.set(domain, {
          domain,
          isFirstParty,
          count: 1,
          totalSize: entry.response.content.size || 0,
          totalTime: entry.time || 0,
        });
      }
    });

    const all = Array.from(domainMap.values());
    const firstParty = all.filter(d => d.isFirstParty).sort((a, b) => b.count - a.count);
    const thirdParty = all.filter(d => !d.isFirstParty).sort((a, b) => b.count - a.count);

    return { firstParty, thirdParty, primaryDomain };
  }, [filteredEntries, har]);

  const firstPartyTotal = {
    count: firstParty.reduce((sum, d) => sum + d.count, 0),
    size: firstParty.reduce((sum, d) => sum + d.totalSize, 0),
    time: firstParty.reduce((sum, d) => sum + d.totalTime, 0),
  };

  const thirdPartyTotal = {
    count: thirdParty.reduce((sum, d) => sum + d.count, 0),
    size: thirdParty.reduce((sum, d) => sum + d.totalSize, 0),
    time: thirdParty.reduce((sum, d) => sum + d.totalTime, 0),
  };

  const totalRequests = firstPartyTotal.count + thirdPartyTotal.count;
  const thirdPartyPercent = totalRequests > 0 ? Math.round((thirdPartyTotal.count / totalRequests) * 100) : 0;

  return (
    <div className="card p-4 h-[450px] flex flex-col">
      <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1">
        {/* Summary bar */}
        <div className="mb-4">
          <div className="flex h-3 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-700 border border-black/5 dark:border-white/5">
            <div
              className="bg-brand-500 transition-all duration-500"
              style={{ width: `${100 - thirdPartyPercent}%` }}
              title={`First-party: ${firstPartyTotal.count} requests`}
            />
            <div
              className="bg-orange-500 transition-all duration-500"
              style={{ width: `${thirdPartyPercent}%` }}
              title={`Third-party: ${thirdPartyTotal.count} requests`}
            />
          </div>
          <div className="flex justify-between mt-1 text-[10px] uppercase font-bold text-slate-400">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-brand-500"></span>
              First-party ({firstPartyTotal.count})
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-orange-500"></span>
              Third-party ({thirdPartyTotal.count})
            </span>
          </div>
        </div>

        {/* Stats comparison */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="p-2 bg-brand-50 dark:bg-brand-500/10 rounded border border-brand-100 dark:border-brand-500/20">
            <div className="text-[10px] uppercase font-bold text-brand-600 dark:text-brand-400 mb-1">First-Party</div>
            <div className="text-sm font-bold text-slate-700 dark:text-slate-300">{formatBytes(firstPartyTotal.size)}</div>
            <div className="text-[10px] text-slate-500 font-medium">{formatTime(firstPartyTotal.time)} total</div>
          </div>
          <div className="p-2 bg-orange-50 dark:bg-orange-500/10 rounded border border-orange-100 dark:border-orange-500/20">
            <div className="text-[10px] uppercase font-bold text-orange-600 dark:text-orange-400 mb-1">Third-Party</div>
            <div className="text-sm font-bold text-slate-700 dark:text-slate-300">{formatBytes(thirdPartyTotal.size)}</div>
            <div className="text-[10px] text-slate-500 font-medium">{formatTime(thirdPartyTotal.time)} total</div>
          </div>
        </div>

        {/* Third-party domains */}
        {thirdParty.length > 0 && (
          <div>
            <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
              Third-Party Domains ({thirdParty.length})
            </h3>
            <div className="space-y-1">
              {thirdParty.slice(0, 20).map((domain) => (
                <div
                  key={domain.domain}
                  className="flex items-center justify-between p-2 rounded bg-slate-50 dark:bg-slate-800/50 text-xs border border-black/5 dark:border-white/5"
                >
                  <div className="flex items-center gap-2 truncate">
                    <ExternalLink className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    <span className="text-slate-700 dark:text-slate-300 truncate font-medium">{domain.domain}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-500 flex-shrink-0 font-bold text-[10px]">
                    <span>{domain.count} req</span>
                    <span>{formatBytes(domain.totalSize)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
